//----------------------------------------------------------------------------------------------------------------------
// SkewedAspect Game Engine
//----------------------------------------------------------------------------------------------------------------------

// Interfaces
import type { GameCanvas, RenderEngineOptions, SageOptions } from './interfaces/game.ts';

// Base Classes
import { DebugConsole } from './classes/debugConsole.ts';
import { GameEngine } from './classes/gameEngine.ts';
import { GameEventBus } from './classes/eventBus.ts';

// Engines
import { AudioEngine } from './engines/audio.ts';
import { SceneEngine } from './engines/scene.ts';

// Managers
import { AssetManager } from './managers/asset.ts';
import { BindingManager } from './managers/binding.ts';
import { GameManager } from './managers/game.ts';
import { GameEntityManager } from './managers/entity.ts';
import { LevelManager } from './managers/level.ts';
import { AudioManager } from './managers/audio.ts';
import { SaveManager } from './managers/save.ts';
import { ColliderDebugManager } from './managers/colliderDebug.ts';
import { UserInputManager } from './managers/input.ts';

// Utils
import { createEngine } from './utils/graphics.ts';
import { createHavokPlugin, initHavok } from './utils/physics.ts';
import { LoggingUtility } from './utils/logger.ts';
import { RaycastHelper } from './utils/raycast.ts';
import { GameTimer } from './utils/timer.ts';
import { VERSION } from './utils/version.ts';
import type { GameEntityDefinition } from './interfaces/entity.ts';

// Debug
import { registerBuiltins } from './debug/builtins.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Creates and initializes a fully-wired GameEngine with rendering, physics, input, and entity management.
 *
 * @param canvas
 * @param entities - Entity definitions to register before the engine starts
 * @param options
 */
export async function createGameEngine(
    canvas : GameCanvas,
    entities : GameEntityDefinition[],
    options : SageOptions = {}
) : Promise<GameEngine>
{
    // Initialize logging
    const logger = new LoggingUtility(options.logLevel || 'debug');
    const engineLogger = logger.getLogger('SAGE');

    engineLogger.info(`Initializing SAGE Game Engine v${ VERSION }...`);

    // Initialize BabylonJS stuff
    engineLogger.debug('Creating rendering engine...');

    const renderOptions : RenderEngineOptions = {
        antialias: true,
        adaptToDeviceRatio: true,
        ...options.renderOptions,
    };

    const engine = await createEngine(canvas, renderOptions, options.largeWorldRendering ?? false);

    engineLogger.debug('Creating physics engine...');
    const havok = await initHavok();
    const physics = createHavokPlugin(havok);

    // Initialize internals
    engineLogger.debug('Creating event bus...');
    const eventBus = new GameEventBus(logger);

    // Initialize SAGE Engines
    engineLogger.debug('Creating scene engine...');
    const sceneEngine = new SceneEngine(canvas, engine, havok, logger);

    // Initialize SAGE Managers
    engineLogger.debug('Creating managers...');
    const assetManager = new AssetManager(eventBus, sceneEngine, logger);
    const inputManager = new UserInputManager(eventBus, canvas as HTMLElement, logger);
    const bindingManager = new BindingManager(eventBus, logger);
    const entityManager = new GameEntityManager(eventBus, logger, bindingManager);
    const levelManager = new LevelManager(eventBus, logger);
    const saveManager = new SaveManager(entityManager, levelManager, logger);
    const gameManager = new GameManager(engine, eventBus, entityManager, inputManager, levelManager, logger);

    // Initialize debug tools
    let debugConsole : DebugConsole | null = null;

    if(options.debug !== false)
    {
        engineLogger.debug('Creating debug console...');
        const namespaceName = (typeof options.debug === 'object' ? options.debug.namespace : undefined) ?? 'sage';
        debugConsole = new DebugConsole(namespaceName);
    }

    engineLogger.debug('Creating collider debug manager...');
    const colliderDebugManager = new ColliderDebugManager();

    // Initialize raycasting
    engineLogger.debug('Creating raycast helper...');
    const raycast = new RaycastHelper(entityManager);

    // Initialize timer
    engineLogger.debug('Creating game timer...');
    const timer = new GameTimer();
    gameManager.registerFrameCallback((dt) => timer.tick(dt * 1000));

    // Initialize audio system (opt-in)
    let audioEngine : AudioEngine | undefined;
    let audioManager : AudioManager | undefined;

    if(options.audioChannels && options.audioChannels.length > 0)
    {
        engineLogger.debug('Creating audio engine...');
        audioEngine = new AudioEngine(logger);
        await audioEngine.initialize();

        engineLogger.debug('Creating audio manager...');
        audioManager = new AudioManager(audioEngine, logger);
        await audioManager.initialize(options.audioChannels);
    }

    engineLogger.info(`SAGE Game Engine v${ VERSION } initialized successfully.`);

    // Register entities
    engineLogger.debug('Loading entities...');
    for(const entity of entities)
    {
        entityManager.registerEntityDefinition(entity);
    }

    // Register default input bindings
    engineLogger.debug('Registering default input bindings...');
    if(options.bindings)
    {
        for(const binding of options.bindings)
        {
            bindingManager.registerBinding(binding);
        }
    }

    const ge = new GameEngine(
        canvas,
        engine,
        physics,
        eventBus,
        logger,
        raycast,
        timer,

        // Engines
        {
            sceneEngine,
            audioEngine,
        },

        // Managers
        {
            assetManager,
            bindingManager,
            entityManager,
            gameManager,
            inputManager,
            levelManager,
            saveManager,
            audioManager,
        },

        options.largeWorldRendering ?? false,

        // Debug tools
        {
            colliders: colliderDebugManager,
            console: debugConsole,
            expose: (name : string, value : unknown) =>
            {
                debugConsole?.expose(name, value);
            },
        }
    );

    // Late-bind: these managers are constructed before GameEngine (because GameEngine takes them as
    // constructor args), but they need a back-reference at runtime. This must happen before any level
    // loads or entity attaches, so do it immediately after construction. Do not reorder.
    entityManager.$setGameEngine(ge);
    levelManager.$setGameEngine(ge);

    // Register debug console built-ins
    if(debugConsole)
    {
        registerBuiltins(debugConsole, ge);
    }

    return ge;
}

//----------------------------------------------------------------------------------------------------------------------
// Exports
//----------------------------------------------------------------------------------------------------------------------

export * from './interfaces/action.ts';
export * from './interfaces/binding.ts';
export * from './interfaces/entity.ts';
export * from './interfaces/game.ts';
export * from './interfaces/input.ts';
export * from './interfaces/logger.ts';

// Export base classes
export { AssetManager, AudioEngine, GameEngine, GameEventBus, SceneEngine };

export * from './classes/eventBus.ts';
export * from './classes/entity.ts';
export * from './classes/level.ts';
export * from './classes/gameLevel.ts';
export * from './behaviors/index.ts';
export * from './events/index.ts';
export * from './handlers/index.ts';
export * from './interfaces/level.ts';
export * from './interfaces/lifecycle.ts';
export * from './utils/logger.ts';
export * from './utils/metadata.ts';
export * from './utils/timer.ts';
export * from './utils/version.ts';

// Export manager types
export type { FrameCallback } from './managers/game.ts';
export type { CreateEntityOptions } from './managers/entity.ts';
export type { TransitionOptions } from './managers/level.ts';
export { AudioManager } from './managers/audio.ts';
export type { ChannelInfo } from './managers/audio.ts';
export { ColliderDebugManager } from './managers/colliderDebug.ts';
export type { ColliderDebugConfig } from './managers/colliderDebug.ts';
export { OutlineManager } from './managers/outline.ts';
export { SaveManager } from './managers/save.ts';
export type { SaveData, SerializedEntity } from './managers/save.ts';

// Export debug tools
export { DebugConsole } from './classes/debugConsole.ts';
export { createEntityProxy } from './utils/entityProxy.ts';

// Export utilities
export { generateId } from './utils/id.ts';
export { RaycastHelper } from './utils/raycast.ts';
export type { EntityPickResult, EntityPickFilter } from './utils/raycast.ts';
export { StateMachine } from './utils/stateMachine.ts';
export {
    getCanonicalTransform,
    toQuatObject, toQuaternion, toVec3Object, toVector3,
} from './utils/vectors.ts';

//----------------------------------------------------------------------------------------------------------------------
